import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { useTrackers } from '@ps/web-shared-analytics';
import { InfoTooltip, StarsButton } from '@ps/web-shared-components';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BalanceAmount from '@swf/components/CurrencyAmount/BalanceAmount';
import StarsRewards from '@swf/components/StarsRewards/StarsRewards';
import AccountIcon from '@swf/core/assets/icons/account-selected.svg';
import SessionSummaryIcon from '@swf/core/assets/icons/alternates/info.svg';
import useFeature from '@swf/core/hooks/useFeature';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import { LoyaltyWidgetLobby } from '@swf/martech';

import { TXTCLI_Current_Session, TXTCLI_Deposit } from './LoggedIn.messages.json';
import styles from './LoggedIn.scss';

const LoggedIn: React.FC = () => {
  const loyaltyWidgetRefresh = useFeature(FeatureFlag.LoyaltyWidgetRefresh);
  const site = useSelector(select.cms.getSiteId);
  const isPoker = useSelector(select.cms.getBrandId) === BrandTypes.POKER_STARS;
  const userId = useSelector(select.account.getUserId);
  const playChips = useSelector(select.account.getPlayChips);
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const { showBalance } = useSelector(select.localSettings.get);
  const totalBalance = useSelector(select.account.getTotalBalance);
  const loading = useSelector((rootState: RootState) => !!rootState.loading?.models.auth);
  const isSportsSelfExcluded = useSelector(select.account.isSportsSelfExcluded);
  const balance = isPlayMoney ? playChips : totalBalance;
  const { buttonPressed } = useTrackers();

  const { modal } = useDispatch<Dispatch>();

  return userId && !loading ? (
    <>
      {(isPoker || Sites.CA_ON === site) &&
        (loyaltyWidgetRefresh ? <LoyaltyWidgetLobby /> : <StarsRewards />)}
      {[Sites.CA_ON, Sites.NL].includes(site) && (
        <div className={styles.summaryWrapper}>
          <InfoTooltip addHoverEffect offset={[0, 15]} content={[TXTCLI_Current_Session]}>
            <StarsButton
              className={styles.summaryIcon}
              icon={<SessionSummaryIcon />}
              onClick={() => {
                buttonPressed({ buttonName: 'Session Summary' });
                modal.enqueue({ path: '/notifications/session-summary-notification' });
              }}
            />
          </InfoTooltip>
        </div>
      )}
      {showBalance && (
        <button
          className={styles.balance}
          onClick={() => {
            buttonPressed({ buttonName: 'Balance' });
            modal.enqueue({ path: `/account/cashier/${isPlayMoney ? 'playmoney' : 'home'}` });
          }}
        >
          <BalanceAmount value={balance as number} decimalClassName={styles.decimal} />
        </button>
      )}
      <button
        className={styles.account}
        onClick={() => {
          buttonPressed({ buttonName: 'Account' });
          modal.enqueue({ path: '/account' });
        }}
      >
        <AccountIcon className={styles.icon} />
        <span className={styles.userId}>{userId}</span>
      </button>
      {!isSportsSelfExcluded && (
        <StarsButton
          className={styles.deposit}
          size="small"
          onClick={() => modal.enqueue({ path: '/account/cashier/deposit' })}
          translation={TXTCLI_Deposit}
        />
      )}
    </>
  ) : null;
};

export default LoggedIn;
